import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Sparkles } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

import { STUDIO_FORMATS, STUDIO_LOOKS, StudioFormat, StudioLook } from '../studio-contract';
import { aspectValue, formatLabel, lookLabel } from '../labels';

/** The frame of a format, drawn: a small outlined rectangle of its proportions. */
export const FormatGlyph: React.FC<{ format: StudioFormat; className?: string }> = ({ format, className }) => {
    const r = aspectValue(format);
    const w = r >= 1 ? 14 : Math.round(14 * r);
    const h = r >= 1 ? Math.round(14 / r) : 14;
    return (
        <span className={cn('inline-flex size-4 shrink-0 items-center justify-center', className)} aria-hidden>
            <span className="rounded-[2px] border-[1.5px] border-current" style={{ width: w, height: h }} />
        </span>
    );
};

const formatName = (t: (k: string, d: string) => string, f: StudioFormat) => {
    switch (f) {
        case '9:16': return t('STUDIO.FORMAT.REEL_SHORT', 'Reel');
        case '4:5': return t('STUDIO.FORMAT.FEED_SHORT', 'Post');
        case '1:1': return t('STUDIO.FORMAT.SQUARE_SHORT', 'Square');
        default: return t('STUDIO.FORMAT.WIDE_SHORT', 'Wide');
    }
};

/**
 * The production's format, as a compact pill that opens the four choices —
 * each with its frame drawn and where it is used. Shared by the Studio home
 * and the production's toolbar.
 */
export const FormatMenu: React.FC<{ value: StudioFormat; onChange: (f: StudioFormat) => void; compact?: boolean }> = ({ value, onChange, compact }) => {
    const { t } = useTranslation();
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5 font-normal" aria-label={t('STUDIO.FORMAT.LABEL', 'Format')}>
                    <FormatGlyph format={value} />
                    <span className="font-medium">{formatName(t, value)}</span>
                    {!compact && <span className="text-muted-foreground">{value}</span>}
                    <ChevronDown className="size-3 text-muted-foreground" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
                <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">{t('STUDIO.FORMAT.LABEL', 'Format')}</DropdownMenuLabel>
                {STUDIO_FORMATS.map((f) => (
                    <DropdownMenuItem key={f} onSelect={() => onChange(f)} className="gap-2.5 py-2">
                        <span className="flex size-7 items-center justify-center rounded-md border bg-background text-muted-foreground"><FormatGlyph format={f} /></span>
                        <span className="min-w-0 flex-1">
                            <span className="block text-sm text-foreground">{formatName(t, f)} <span className="text-muted-foreground">· {f}</span></span>
                            <span className="block text-xs text-muted-foreground">{formatLabel(t, f).hint}</span>
                        </span>
                        {f === value && <Check className="size-4 text-foreground" />}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

/** The production's look: the photographic style every shot is made in. */
export const LookMenu: React.FC<{ value: StudioLook; onChange: (l: StudioLook) => void }> = ({ value, onChange }) => {
    const { t } = useTranslation();
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5 font-normal" aria-label={t('STUDIO.LOOK.LABEL', 'Look')}>
                    <Sparkles className="size-3.5 text-muted-foreground" />
                    <span className="font-medium">{lookLabel(t, value).label}</span>
                    <ChevronDown className="size-3 text-muted-foreground" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72">
                <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">{t('STUDIO.LOOK.LABEL', 'Look')}</DropdownMenuLabel>
                {STUDIO_LOOKS.map((l) => (
                    <DropdownMenuItem key={l} onSelect={() => onChange(l)} className="gap-2.5 py-2">
                        <span className="min-w-0 flex-1">
                            <span className="block text-sm text-foreground">{lookLabel(t, l).label}</span>
                            <span className="block text-xs text-muted-foreground">{lookLabel(t, l).hint}</span>
                        </span>
                        {l === value && <Check className="size-4 text-foreground" />}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
